// Converts any input into a number safely. If conversion fails returns 0 instead of NaN.
const safeNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

// Normalizes discount values.
const toRate = (value) => {
    const parsed = safeNumber(value);
    if (parsed > 1) {
        return parsed / 100;
    }
    return parsed;
};

// Applies pricing psychology / rounding strategy based on mode.
const toRoundedPrice = (value, mode) => {
    if (mode === 1) return Math.ceil(value);
    if (mode === 2) return Math.round(value);
    if (mode === 3) return Math.floor(value) + 0.95;
    return value;
};

// Converts a sheet-priced item into price per square meter.
const getSheetSqmPrice = (item = {}) => {
    const sheetWidth = safeNumber(item?.width);
    const sheetHeight = safeNumber(item?.height);
    const sheetArea = (sheetWidth * sheetHeight) / 1000000;

    if (!sheetArea) return 0;

    const sheetPrice = safeNumber(item?.sheet_price ?? item?.price);
    if (!sheetPrice) return 0;

    return sheetPrice / sheetArea;
};

// Converts a service/extra record into a cost using explicit cost first,
// otherwise labour minutes according to the algorithm.
const getServiceCost = (item, labourMinuteRate) => {
    if (typeof item === "number") {
        return safeNumber(item);
    }

    if (!item || typeof item !== "object") {
        return 0;
    }

    const explicitCost = safeNumber(item?.cost ?? item?.price);
    if (explicitCost) {
        return explicitCost;
    }

    const unitMinutes = safeNumber(item?.unit_minutes ?? item?.minutes);
    return unitMinutes * labourMinuteRate;
};

export const totalPrice = ({
    qty = 1,
    opening_width,
    opening_height,
    width,
    height,
    mat = {},
    frame = {},
    glass = {},
    backing = {},
    fitting = null,
    extras = [],
    openings = 1,
    opening_unit_cost = 5,
    discount = 0,
    override_total,
    rounding_mode = 0,
    control = {}
}) => {
    /* ================= MASTER CONSTANTS ================= */
    const RetailMU = safeNumber(control?.retail_mu ?? 2.8);
    const MaterialMarkup = safeNumber(control?.material_markup ?? 1.3);
    const LabourMarkup = safeNumber(control?.labour_markup ?? 2.0);
    const LabourRate = safeNumber(control?.labour_rate ?? 42);
    const LabourMinuteRate = (LabourRate * LabourMarkup) / 60;

    const SawCutWidth = safeNumber(control?.saw_cut_width ?? 3.5);
    const FramingTime = safeNumber(control?.framing_time ?? 5);
    const GlassTime = safeNumber(control?.glass_time ?? 5);
    const MatTime = safeNumber(control?.mat_time ?? 5);
    const BackingTime = safeNumber(control?.backing_time ?? 5);

    const WrappingMaterial = safeNumber(control?.wrapping_material ?? 0.85);
    const WrappingTime = safeNumber(control?.wrapping_time ?? 1);

    const defaultWastagePercent = safeNumber(control?.default_wastage_percent ?? 30);
    const WastageFactor =
        defaultWastagePercent >= 100 ? 0 : 100 / (100 - defaultWastagePercent);

    const OpeningUnitCost = safeNumber(
        control?.opening_unit_cost ?? opening_unit_cost
    );

    /* ================= GEOMETRY ENGINE ================= */
    const openingWidth = safeNumber(opening_width ?? width);
    const openingHeight = safeNumber(opening_height ?? height);
    const matTop = safeNumber(mat?.top);
    const matBottom = safeNumber(mat?.bottom);
    const matSide = safeNumber(mat?.side);

    const ExtWidth = openingWidth + (2 * matSide);
    const ExtHeight = openingHeight + matTop + matBottom;
    const Area = (ExtWidth * ExtHeight) / 1000000;
    const BasePerimeter = (2 * (ExtWidth + ExtHeight)) / 1000;

    /* ================= FRAME ================= */
    const mouldingWidth = safeNumber(frame?.width);
    const frameWastagePercent = safeNumber(frame?.wastage) || defaultWastagePercent;
    const frameWastageFactor =
        frameWastagePercent >= 100 ? 0 : 100 / (100 - frameWastagePercent);

    const frameLoading = safeNumber(frame?.loading) || MaterialMarkup;

    const Allowance = 8 * (mouldingWidth + SawCutWidth);
    const AdjustedPerimeter = ((BasePerimeter * 1000) + Allowance) / 1000;
    const EffectiveLength = AdjustedPerimeter * frameWastageFactor;

    const frameListPrice = safeNumber(frame?.price);
    const FrameMaterial = frameListPrice
        ? EffectiveLength * frameListPrice * frameLoading
        : 0;
    const FrameLabour = frameListPrice
        ? FramingTime * LabourMinuteRate
        : 0;

    /* ================= MAT ================= */
    const matSqMPrice = getSheetSqmPrice(mat);
    const MatMaterial = matSqMPrice
        ? Area * matSqMPrice * WastageFactor * MaterialMarkup
        : 0;
    const MatLabour = matSqMPrice
        ? MatTime * LabourMinuteRate
        : 0;

    /* ================= GLASS ================= */
    const glassSqMPrice = getSheetSqmPrice(glass);
    const GlassMaterial = glassSqMPrice
        ? Area * glassSqMPrice * WastageFactor * MaterialMarkup
        : 0;
    const GlassLabour = glassSqMPrice
        ? GlassTime * LabourMinuteRate
        : 0;

    /* ================= BACKING ================= */
    const backingSqMPrice = getSheetSqmPrice(backing);
    const BackingMaterial = backingSqMPrice
        ? Area * backingSqMPrice * WastageFactor * MaterialMarkup
        : 0;
    const BackingLabour = backingSqMPrice
        ? BackingTime * LabourMinuteRate
        : 0;

    /* ================= FITTING / EXTRAS ================= */
    const FittingCost = getServiceCost(fitting, LabourMinuteRate);

    const ExtrasCost = Array.isArray(extras)
        ? extras.reduce((sum, extra) => {
            return sum + getServiceCost(extra, LabourMinuteRate);
        }, 0)
        : getServiceCost(extras, LabourMinuteRate);

    /* ================= WRAPPING ================= */
    const WrappingCost =
        AdjustedPerimeter * (WrappingMaterial + (WrappingTime * LabourMinuteRate));

    /* ================= OPENINGS ================= */
    const OpeningCost =
        Math.max(0, safeNumber(openings) - 1) * OpeningUnitCost;

    /* ================= TOTAL BASE PRICE ================= */
    const basePriceSingle =
        FrameMaterial + FrameLabour +
        MatMaterial + MatLabour +
        GlassMaterial + GlassLabour +
        BackingMaterial + BackingLabour +
        FittingCost +
        ExtrasCost +
        WrappingCost +
        OpeningCost;

    const quantity = Math.max(1, safeNumber(qty));
    const BasePrice = basePriceSingle * quantity;

    /* ================= RETAIL + DISCOUNT + OVERRIDE ================= */
    const RetailPrice = BasePrice * RetailMU;
    const discountRate = Math.max(0, toRate(discount));
    const DiscountedPrice = RetailPrice * (1 - discountRate);
    const FinalPreRound = typeof override_total === "number"
        ? override_total
        : DiscountedPrice;
    const FinalPrice = toRoundedPrice(FinalPreRound, safeNumber(rounding_mode));

    return {
        algorithm: "EngineeredPriceConstruction",
        geometry: {
            openingWidth,
            openingHeight,
            extWidth: ExtWidth,
            extHeight: ExtHeight,
            area: Area,
            basePerimeter: BasePerimeter,
            adjustedPerimeter: AdjustedPerimeter,
            effectiveLength: EffectiveLength
        },
        breakdown: {
            FrameMaterial,
            FrameLabour,
            MatMaterial,
            MatLabour,
            GlassMaterial,
            GlassLabour,
            BackingMaterial,
            BackingLabour,
            FittingCost,
            ExtrasCost,
            WrappingCost,
            OpeningCost
        },
        basePriceSingle,
        basePrice: BasePrice,
        retailPrice: RetailPrice,
        discountedPrice: DiscountedPrice,
        finalPrice: FinalPrice
    };
};